import type { FunctionComponent, ReactNode } from 'react';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { FormattedName } from '../../components/FormattedName';
import { GenericAssignReviewerBadge } from '../../components/ReviewerComponents';
import type { AuditTrailStep } from './types';

const resolveActorFallback = (
    step: AuditTrailStep,
    unknownUserLabel: string,
    pendingLabel: string,
    noPublisherLabel: string,
): string => {
    if (step.state !== 'upcoming') {
        return unknownUserLabel;
    }
    if (step.id === 'published') {
        return noPublisherLabel;
    }
    return pendingLabel;
};

export const AuditActorName: FunctionComponent<{
    readonly noPublisherLabel: string;
    readonly pendingLabel: string;
    readonly step: AuditTrailStep;
    readonly textClass: string;
    readonly textSizeClass: string;
    readonly unknownUserLabel: string;
}> = ({ noPublisherLabel, pendingLabel, step, textClass, textSizeClass, unknownUserLabel }) => (
    <span className={`truncate ${textSizeClass} font-medium tracking-tight ${textClass}`}>
        {step.actor === null ? (
            resolveActorFallback(step, unknownUserLabel, pendingLabel, noPublisherLabel)
        ) : (
            <FormattedName defaultText={unknownUserLabel} reviewer={step.actor} />
        )}
    </span>
);

// Renders the actor for a row: an inline "assign reviewer" pill when a reviewer is
// still expected, the formatted name when known, or a state-specific fallback label.
export const renderUserPrimary = (
    step: AuditTrailStep,
    architectureId: string,
    item: JobItemDetails,
    isArchived: boolean,
    onInviteReviewer: (() => void) | undefined,
    rowTextClass: string,
    unknownUserLabel: string,
    pendingLabel: string,
    noPublisherLabel: string,
    showReviewerPill: boolean,
    badgeHeightClass = '!h-[26px]',
    textSizeClass = 'text-sm',
): ReactNode => {
    if (showReviewerPill) {
        return (
            <GenericAssignReviewerBadge
                badgeClassName={`${badgeHeightClass} tracking-tight hover:!border-gray-400`}
                chromeVariant="gray"
                disabled={isArchived}
                item={item}
                itemType={item.type}
                jobArchitectureId={architectureId}
                jobClusterId={item.parentId}
                onInvite={onInviteReviewer}
                onlyAssignReviewer={true}
                showOnlyWhenUnassigned={false}
            />
        );
    }
    return (
        <AuditActorName
            noPublisherLabel={noPublisherLabel}
            pendingLabel={pendingLabel}
            step={step}
            textClass={rowTextClass}
            textSizeClass={textSizeClass}
            unknownUserLabel={unknownUserLabel}
        />
    );
};
